import { useNotifications } from '@armhr/ui';
import { LoadingButton } from '@mui/lab';
import { Box, Container, Link, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { useApi } from '../../hooks/useApi';

const OnboardingNewTabPage: React.FC = () => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const autoOpenAttempted = useRef(false);

  const openOnboarding = async () => {
    try {
      setLoading(true);
      // Mint a fresh single-use SSO token per attempt so it is never stale or
      // already-consumed by the time the new tab loads it.
      const { results: prismUrl } = await api.onboarding.getPrehireRedirect();

      if (!prismUrl) {
        showNotification({ severity: 'error', message: true });
        return;
      }

      const newWindow = window.open(prismUrl, '_blank');

      if (newWindow === null) {
        showNotification({
          severity: 'error',
          message: (
            <>
              Opening onboarding in a new tab was blocked. Please allow popups
              and try again, or click the link below to continue:{' '}
              <Link href={prismUrl} target="_blank" rel="noopener">
                {prismUrl}
              </Link>
            </>
          ),
        });
      }
    } catch {
      showNotification({ severity: 'error', message: true });
    } finally {
      setLoading(false);
    }
  };

  // Best-effort auto-open on mount. Popup blockers often stop window.open
  // without a user gesture; the button below is the reliable fallback and
  // fetches its own fresh token when clicked.
  useEffect(() => {
    if (autoOpenAttempted.current) {
      return;
    }
    autoOpenAttempted.current = true;
    openOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Helmet>
        <title>Onboarding | Armhr</title>
      </Helmet>
      <Box sx={{ textAlign: 'center', pt: 8, maxWidth: 560, mx: 'auto' }}>
        <Typography variant="h4" mb={2}>
          Continue your onboarding
        </Typography>
        <Typography variant="body1" color="grey.600" mb={4}>
          Your onboarding opens in a new tab. If it didn't open automatically,
          use the button below to continue.
        </Typography>
        <LoadingButton
          size="large"
          variant="contained"
          color="primary"
          loading={loading}
          onClick={openOnboarding}
        >
          Open Onboarding
        </LoadingButton>
      </Box>
    </Container>
  );
};

export default OnboardingNewTabPage;
